import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnboardingInput, Place } from '../api/types';
import { errorMessage } from '../lib/errors';
import { useServices } from '../services';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'];
const TYPES: { value: OnboardingInput['business_type']; label: string }[] = [
  { value: 'food_truck', label: 'Food truck' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'cafe', label: 'Café' },
  { value: 'other', label: 'Other' },
];

function storedName(): string {
  try {
    return sessionStorage.getItem('dp-owner-name') ?? '';
  } catch {
    return '';
  }
}

/** Shown to a signed-in person who has no business yet: sets up the business, first site and trial. */
export function OnboardingPage() {
  const { api, auth } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<OnboardingInput>({
    owner_name: storedName(), business_name: '', business_type: 'food_truck', site_name: '', suburb: '', state: 'NSW',
    open_time: '11:00', close_time: '22:00', trading_days: [0, 1, 2, 3, 4, 5, 6],
    global_model: false, benchmarks: false, accept_terms: false,
  });
  const [places, setPlaces] = useState<Place[]>([]);
  const [picked, setPicked] = useState<Place | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof OnboardingInput>(k: K, v: OnboardingInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Suburb suggestions (debounced) so the forecast uses the right local weather
  useEffect(() => {
    const q = form.suburb.trim();
    if (q.length < 2 || (picked && picked.name === q)) { setPlaces([]); return; }
    const t = setTimeout(() => { api.findPlaces(q).then(setPlaces).catch(() => setPlaces([])); }, 300);
    return () => clearTimeout(t);
  }, [form.suburb, picked, api]);

  const choose = (p: Place) => {
    setPicked(p);
    setPlaces([]);
    setForm((f) => ({ ...f, suburb: p.name, state: p.state, site_name: f.site_name || p.name }));
  };

  const toggleDay = (d: number) =>
    set('trading_days', form.trading_days.includes(d) ? form.trading_days.filter((x) => x !== d) : [...form.trading_days, d].sort());

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.business_name.trim() || !form.owner_name.trim()) return setError('Please fill in your name and your business name.');
    if (!form.suburb.trim()) return setError('Please enter the suburb your site trades in.');
    if (!form.trading_days.length) return setError('Choose at least one trading day.');
    if (!form.accept_terms) return setError('Please accept the trial terms to continue.');
    setBusy(true);
    try {
      const input: OnboardingInput = { ...form, site_name: form.site_name.trim() || form.suburb.trim() };
      if (picked && picked.name === form.suburb && picked.state === form.state) {
        Object.assign(input, { latitude: picked.latitude, longitude: picked.longitude, timezone: picked.timezone });
      }
      await api.completeOnboarding(input);
      await qc.invalidateQueries({ queryKey: ['me'] });
      navigate('/upload?welcome=1', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <form className="auth-card onboarding-card" onSubmit={submit} noValidate>
        <div className="wordmark" aria-label="DemandPilot">Demand<span>Pilot</span></div>
        <h1 style={{ fontSize: 24, marginBottom: 6 }}>Set up your business</h1>
        <p className="muted small" style={{ marginBottom: 18 }}>Two minutes. You can change all of this later in Settings.</p>
        {error && <p className="form-error" role="alert">{error}</p>}

        <div className="field">
          <label htmlFor="ob-owner">Your name</label>
          <input id="ob-owner" value={form.owner_name} onChange={(e) => set('owner_name', e.target.value)} autoComplete="name" required />
        </div>
        <div className="field">
          <label htmlFor="ob-biz">Business name</label>
          <input id="ob-biz" value={form.business_name} onChange={(e) => set('business_name', e.target.value)} autoComplete="organization" required autoFocus />
        </div>
        <div className="field">
          <label htmlFor="ob-type">Type of business</label>
          <select id="ob-type" value={form.business_type} onChange={(e) => set('business_type', e.target.value as OnboardingInput['business_type'])}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <fieldset className="ob-group">
          <legend>Your first site</legend>
          <div className="ob-row">
            <div className="field" style={{ position: 'relative', flex: 2 }}>
              <label htmlFor="ob-suburb">Suburb</label>
              <input id="ob-suburb" value={form.suburb} autoComplete="off" required aria-autocomplete="list"
                onChange={(e) => { set('suburb', e.target.value); setPicked(null); }} />
              {places.length > 0 && (
                <ul className="ob-suggest" role="listbox" aria-label="Suburb suggestions">
                  {places.map((p) => (
                    <li key={`${p.name}-${p.state}-${p.postcode}`}>
                      <button type="button" onClick={() => choose(p)}>{p.name} <span className="muted">{p.state} {p.postcode}</span></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="ob-state">State</label>
              <select id="ob-state" value={form.state} onChange={(e) => set('state', e.target.value)}>
                {STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label htmlFor="ob-site">Site name</label>
            <input id="ob-site" placeholder={form.suburb || 'e.g. Bigge Park'} value={form.site_name} onChange={(e) => set('site_name', e.target.value)} />
            <span className="hint">How your team refers to this truck, shop or venue.</span>
          </div>
          <div className="field">
            <span className="label">Trading days</span>
            <div className="ob-days" role="group" aria-label="Trading days">
              {DAYS.map((d, i) => (
                <label key={d} className={form.trading_days.includes(i) ? 'on' : ''}>
                  <input type="checkbox" checked={form.trading_days.includes(i)} onChange={() => toggleDay(i)} />{d}
                </label>
              ))}
            </div>
          </div>
          <div className="ob-row">
            <div className="field"><label htmlFor="ob-open">Opens</label><input id="ob-open" type="time" value={form.open_time} onChange={(e) => set('open_time', e.target.value)} /></div>
            <div className="field"><label htmlFor="ob-close">Closes</label><input id="ob-close" type="time" value={form.close_time} onChange={(e) => set('close_time', e.target.value)} /></div>
          </div>
          <span className="hint">Closing after midnight is fine, e.g. 01:00.</span>
        </fieldset>

        <fieldset className="ob-group">
          <legend>Data sharing (optional)</legend>
          <label className="ob-check">
            <input type="checkbox" checked={form.global_model} onChange={(e) => set('global_model', e.target.checked)} />
            <span>Help improve forecasts for similar businesses. Only anonymised demand patterns are shared: never your name, prices or customers.</span>
          </label>
          <label className="ob-check">
            <input type="checkbox" checked={form.benchmarks} onChange={(e) => set('benchmarks', e.target.checked)} />
            <span>Join peer benchmarks, so you can compare with at least four similar businesses. Others only ever see group averages.</span>
          </label>
        </fieldset>

        <label className="ob-check" style={{ marginBottom: 16 }}>
          <input type="checkbox" checked={form.accept_terms} onChange={(e) => set('accept_terms', e.target.checked)} />
          <span>I agree to the <a href="https://demandpilot.novamind.nz/terms.html" target="_blank" rel="noreferrer">trial terms</a> and <a href="https://demandpilot.novamind.nz/privacy.html" target="_blank" rel="noreferrer">privacy policy</a>.</span>
        </label>

        <button type="submit" className="btn btn-primary" style={{ width: '100%', minHeight: 42 }} disabled={busy}>
          {busy ? 'Setting up…' : 'Start my 30-day trial'}
        </button>
        <button type="button" className="link-btn" style={{ marginTop: 12 }} onClick={async () => { await auth.signOut(); qc.clear(); navigate('/sign-in'); }}>
          Sign out
        </button>
      </form>
    </div>
  );
}
